import { useEffect, useId, useRef, useState } from 'react';
import { PLATFORM_LABEL, STATUS_LABEL, fmtCredits, pColor, statusClass } from './lib.jsx';

export function Status({ status }) {
  return <span className={`status ${statusClass(status)}`}>{STATUS_LABEL[status] ?? status}</span>;
}

export function PlatformDot({ p, children }) {
  return <span className="pdot" style={{ '--pc': pColor(p) }}>{children ?? PLATFORM_LABEL[p] ?? p}</span>;
}

export function Wordmark() {
  return <img src="/assets/logo.png" alt="Yapscape" width="121" height="30" />;
}

// Line icons on a 24px grid, drawn in the text colour.
const ICONS = {
  home: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  reports: 'M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM14 3v5h5M9 13h6M9 17h4',
  sample: 'M12 3l1.8 4.9L19 9.7l-5.2 1.8L12 16.5l-1.8-5L5 9.7l5.2-1.8zM18.5 15l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z',
  account: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  plus: 'M12 5v14M5 12h14',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6 6 18',
  out: 'M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H4',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  users: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21a7 7 0 0 1 14 0M16 3.5a4 4 0 0 1 0 7M22 21a7 7 0 0 0-4.5-6.5',
  chart: 'M3 20h18M7 16v-5M12 16V6M17 16v-8',
  flag: 'M5 21V4M5 4h11l-2 4 2 4H5',
  sliders: 'M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1M15 4v4M9 10v4M17 16v4',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  plug: 'M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0zM12 17v4',
};
export function Icon({ name, size = 18 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={ICONS[name]} /></svg>;
}

/** A headline number on a card: a label, the value with an optional unit, and whatever explains it underneath. */
export function StatCard({ label, value, unit, children }) {
  return (
    <div className="stat-card">
      <span className="label">{label}</span>
      <span className="value">{value}{unit ? <small>{unit}</small> : null}</span>
      {children}
    </div>
  );
}

/** Credits used against credits given; red once 85% is used. */
export function CreditMeter({ used, total }) {
  const p = total ? Math.min(100, (used / total) * 100) : 100;
  return (
    <div className="meter" role="img" aria-label={`${fmtCredits(used)} of ${fmtCredits(total)} credits used`}>
      <i style={{ width: `${p}%`, background: p >= 85 ? 'var(--bad)' : 'var(--brand)' }} />
    </div>
  );
}

export function Drawer({ title, onClose, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label={title} tabIndex={-1} ref={ref}>
        <header>
          <h2>{title}</h2>
          <button className="btn small" onClick={onClose}>Close</button>
        </header>
        <div className="body">{children}</div>
      </aside>
    </>
  );
}

export function Toast({ text }) {
  return text ? <div className="toast" role="status">{text}</div> : null;
}

/** Modal dialog on the native <dialog> element: focus trap, Escape and backdrop click for free. */
export function Modal({ title, children, onClose, actions, wide }) {
  const ref = useRef(null);
  useEffect(() => { const d = ref.current; d.showModal(); return () => d.open && d.close(); }, []);
  return (
    <dialog ref={ref} className={wide ? 'modal wide' : 'modal'} onClose={onClose} onClick={(e) => e.target === ref.current && onClose()} aria-label={title}>
      <form method="dialog" onSubmit={(e) => { e.preventDefault(); }}>
        <h2>{title}</h2>
        <div className="modal-body">{children}</div>
        <div className="modal-actions">{actions}</div>
      </form>
    </dialog>
  );
}

/**
 * Text input with a suggestion list (the WAI-ARIA combobox pattern, list autocomplete). Typing searches: `local(q)`
 * answers at once, then `load(q)` (after a short pause, from 2 characters) replaces the list; a reply to an older
 * query is dropped. Focus stays in the input: Down and Up move through the list, Enter picks, Escape closes.
 * Options are { key, label, detail? }; picking calls onPick(option) and does not search again.
 */
export function Combobox({ label, value, onChange, onPick, local, load, note, ...inputProps }) {
  const auto = useId();
  const id = inputProps.id ?? auto;
  const input = useRef(null);
  const seq = useRef(0), typed = useRef(false);
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(null); // key of the highlighted option: it survives the list being replaced
  const i = opts.findIndex((o) => o.key === cur);

  const show = (o) => { setOpts(o); setOpen(o.length > 0 && document.activeElement === input.current); };
  useEffect(() => {
    if (!typed.current) return;
    typed.current = false;
    const q = value.trim(), n = ++seq.current;
    show(q ? local?.(q) ?? [] : []);
    if (!load || q.length < 2) return;
    const t = setTimeout(() => load(q).then((o) => n === seq.current && show(o), () => {}), 200);
    return () => clearTimeout(t);
  }, [value]);
  useEffect(() => { if (open && i >= 0) document.getElementById(`${id}-${i}`)?.scrollIntoView({ block: 'nearest' }); }, [open, i]);

  const pick = (o) => { seq.current++; setOpen(false); setCur(null); onPick(o); };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' && opts.length) { e.preventDefault(); if (!open) { setOpen(true); setCur(opts[0].key); } else setCur(opts[Math.min(i + 1, opts.length - 1)].key); }
    else if (e.key === 'ArrowUp' && open) { e.preventDefault(); setCur(i > 0 ? opts[i - 1].key : null); }
    else if (e.key === 'Enter' && open && i >= 0) { e.preventDefault(); pick(opts[i]); }
    else if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); setCur(null); }
    else inputProps.onKeyDown?.(e);
  };
  return (
    <div className="combo">
      <input {...inputProps} ref={input} id={id} type="text" role="combobox" aria-autocomplete="list" aria-expanded={open}
        aria-controls={`${id}-list`} aria-activedescendant={open && i >= 0 ? `${id}-${i}` : undefined} autoComplete="off"
        value={value} onChange={(e) => { typed.current = true; onChange(e.target.value); }} onKeyDown={onKeyDown}
        onBlur={(e) => { setOpen(false); inputProps.onBlur?.(e); }} />
      {/* mousedown would move focus out of the input and close the list before the click lands */}
      <div className="combo-pop" hidden={!open} onMouseDown={(e) => e.preventDefault()}>
        <ul id={`${id}-list`} role="listbox" aria-label={`${label} suggestions`}>
          {opts.map((o, j) => (
            <li key={o.key} id={`${id}-${j}`} role="option" aria-selected={j === i} onMouseEnter={() => setCur(o.key)} onClick={() => pick(o)}>
              <span className="combo-label">{o.label}</span>{o.detail ? <span className="combo-detail">{o.detail}</span> : null}
            </li>
          ))}
        </ul>
        {note ? <p className="combo-note">{note}</p> : null}
      </div>
      <span className="sr-only" role="status">{open ? `${opts.length} suggestion${opts.length === 1 ? '' : 's'}` : ''}</span>
    </div>
  );
}

/** A report or page section that can be collapsed, with a consistent header (title, one-line summary, chevron). */
export function Section({ id, title, hint, open = true, children }) {
  return (
    <details className="section" id={id} open={open}>
      <summary><span className="sec-title">{title}</span>{hint ? <span className="sec-hint">{hint}</span> : null}<span className="chev" aria-hidden="true" /></summary>
      <div className="sec-body">{children}</div>
    </details>
  );
}
